import IMG_AGE_NOTICE_IMAGE from '~/assets/images/footer/ageNoticeImage.png';

import { useLocale } from '~/shared/libs/gatsby-plugin-i18n';
import { DevsistersCi as _DevsistersCi } from '@devsisters/ui-common/lib/identity';
import styled from '@emotion/styled';
import formatDate from 'date-fns/format';
import { graphql, useStaticQuery } from 'gatsby';
import { rem } from 'polished';
import React from 'react';

import _Button from '~/components/Button';
import _ContentBox from '~/components/ContentBox';
import SocialLinks from '~/widgets/social-links';
import { css, scaledCss } from '~/css';
import { SocialLink, useFrontmatter } from '~/shared/contexts/frontmatter';
import type { FooterDataQuery } from '~/generated/types/gatsby';
import { useCurrentGame } from '~/shared/hooks/games';
import { useLocalizedNode, useTexts } from '~/shared/hooks/i18n';
import { skin } from '~/theme';

interface FooterProps {}

const Container = styled('footer')(
  skin({
    blackSquare: scaledCss({
      background: 'primary',
      paddingTop: rem(44),
      paddingBottom: rem(70),
      fontSize: [70, '70px'],
    }),
  }),
);

const ContentBox = styled(_ContentBox)(
  skin({
    blackSquare: css({
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    }),
  }),
);

const DevsistersCi = styled(_DevsistersCi)(
  skin({
    blackSquare: scaledCss({
      color: '#fff',
      fontSize: [44, '29px'],
    }),
  }),
);

const ButtonWrapper = styled('div')(
  skin({
    blackSquare: css({
      display: 'flex',
      width: ['100%', 'auto'],
      justifyContent: 'center',
      flexDirection: ['column', 'row'],
      marginBottom: 13,
    }),
  }),
);

const ButtonLink = styled(_Button.withComponent('a'))((props: { bold: boolean }) =>
  skin({
    blackSquare: css({
      marginX: [10, 10],
      marginY: [9, 0],
      fontSize: [16, 16],
      paddingX: [30, 30],
      paddingY: [8, 11],
      lineHeight: ['normal', 1],
      fontWeight: props.bold ? 'bold' : 'noraml',
    }),
  }),
);

const InfoWrapper = styled('div')(
  skin({
    blackSquare: css({
      display: 'flex',
      justifyContent: 'center',
      flexWrap: 'wrap',
    }),
  }),
);

const InfoText = styled('span')(
  skin({
    blackSquare: css({
      fontSize: 16,
      marginX: 5,
      marginY: 3,
      textAlign: 'center',
      color: 'primary',
    }),
  }),
);

const AgeNoticeWrapper = styled('div')(
  skin({
    blackSquare: css({
      flexDirection: ['column', 'row'],
      alignItems: ['center', 'initial'],
      display: 'flex',
      marginTop: rem(60),
    }),
  }),
);

const AgeNotiveImg = styled('img')(
  skin({
    blackSquare: css({
      marginBottom: [rem(25), 0],
      width: rem(92),
      height: rem(92),
    }),
  }),
);

const AgeNoticeList = styled('ul')({
  fontSize: rem(14),
  listStyle: 'none',
  padding: 0,
  margin: 0,
  marginLeft: rem(12),
});

const AgeNotiveListItem = styled('li')({
  color: 'white',
  marginBottom: rem(5),
});

const Footer: React.FC<FooterProps> = () => {
  const t = useTexts();
  const data = useStaticQuery<FooterDataQuery>(graphql`
    query FooterData {
      allCompanyInfo {
        nodes {
          locale
          address
          tel
          fax
          terms
          privacy
        }
      }
    }
  `);
  const currentLocale = useLocale();
  const companyInfo = useLocalizedNode(data.allCompanyInfo.nodes);
  const frontmatter = useFrontmatter();
  const socials = frontmatter.footer.socials as unknown as SocialLink[] | undefined;
  const { terms, privacy } = companyInfo;
  const { cs } = frontmatter.navigation;

  const outerLinkProps = { target: '_blank', rel: 'noopener' };

  const ageNotice = useCurrentGame()?.ageNotice;

  return (
    <Container>
      <ContentBox>
        {socials && (
          <div css={{ marginBottom: rem(48) }}>
            <SocialLinks socials={socials} />
          </div>
        )}
        <ButtonWrapper>
          <ButtonLink href={terms} {...outerLinkProps} className='footer-terms-link'>
            {t`footer.terms`}
          </ButtonLink>
          <ButtonLink href={privacy} {...outerLinkProps} bold={currentLocale === 'ko'} className='footer-privacy-link'>
            {t`footer.privacy`}
          </ButtonLink>
          {cs?.url && (
            <ButtonLink href={cs.url} {...outerLinkProps} className='footer-cs-link'>
              {t`cs`}
            </ButtonLink>
          )}
        </ButtonWrapper>

        <InfoWrapper>
          <InfoText>{companyInfo.address}</InfoText>
        </InfoWrapper>

        <InfoWrapper>
          <InfoText>
            {t`footer.tel`}: {companyInfo.tel}
          </InfoText>
          <InfoText>
            {t`footer.fax`}: {companyInfo.fax}
          </InfoText>
        </InfoWrapper>

        <InfoWrapper>
          <InfoText>CookieRun® © {formatDate(Date.now(), 'yyyy')} Devsisters Corp. All Rights Reserved.</InfoText>
        </InfoWrapper>

        <div
          css={css({
            display: 'inline-flex',
            marginTop: 23,
            lineHeight: 1,
          })}
        >
          <DevsistersCi />
        </div>
        {ageNotice && (
          <AgeNoticeWrapper>
            <AgeNotiveImg src={IMG_AGE_NOTICE_IMAGE} />
            <AgeNoticeList>
              {ageNotice.map((v, i) => {
                return <AgeNotiveListItem key={i}>{v}</AgeNotiveListItem>;
              })}
            </AgeNoticeList>
          </AgeNoticeWrapper>
        )}
      </ContentBox>
    </Container>
  );
};

export default Footer;
